import ChannelSelector from "@components/ChannelSelector";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  type PowerQualityResponse,
  type PowerQualityRow,
  type PowerQualitySummary,
  usePowerQuality,
} from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Checkbox, Radio, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

const measurementOptions = [
  { label: "Приём (A+/R+)", value: 1 },
  { label: "Отдача (A-/R-)", value: 2 },
];

const stepOptions = [
  { label: "30 мин", value: 30 },
  { label: "60 мин", value: 60 },
];

const unitOptions = [
  { label: "кВт⋅ч", value: "kw" },
  { label: "МВт⋅ч", value: "mw" },
];

const formatNumber = (value: unknown, digits = 3) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "—";

const buildChannelOptions = (rows?: ChannelMeterRow[]) => {
  if (!rows) {
    return [];
  }
  return rows.map((channel) => ({
    label: `${channel.meter_number} — ${channel.feeder_name ?? "Без названия"}`,
    value: channel.meter_number,
  }));
};

const ReportsPowerQualityPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [measurement, setMeasurement] = useState<number>(1);
  const [step, setStep] = useState<number>(60);
  const [unitMode, setUnitMode] = useState<"kw" | "mw">("kw");
  const [useRegisters, setUseRegisters] = useState<boolean>(false);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("day");
    return { from: start, to: start.add(1, "day") };
  });

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const channelsQuery = useChannelsFlat(objectID, undefined, 500);
  const channelOptions = useMemo(
    () => buildChannelOptions(channelsQuery.data),
    [channelsQuery.data],
  );

  useEffect(() => {
    if (selectedChannel && channelOptions.some((option) => option.value === selectedChannel)) {
      return;
    }
    if (channelOptions.length > 0) {
      setSelectedChannel(channelOptions[0].value);
    } else {
      setSelectedChannel(undefined);
    }
  }, [channelOptions, selectedChannel]);

  const dateISO = period.from?.format("YYYY-MM-DD");
  const useMWT = unitMode === "mw";

  const powerQualityQuery = usePowerQuality({
    region: regionID,
    object: objectID,
    channel: selectedChannel,
    date: dateISO,
    measurement,
    step,
    useRegisters,
    useMWT,
  });

  const payload: PowerQualityResponse | undefined = powerQualityQuery.data;
  const rows = payload?.rows ?? [];
  const summary = payload?.summary ?? [];
  const unit = payload?.unit ?? (useMWT ? "МВт⋅ч" : "кВт⋅ч");
  const reactiveUnit = unit === "МВт⋅ч" ? "Мвар⋅ч" : "квар⋅ч";
  const fullPowerUnit = unit === "МВт⋅ч" ? "МВА" : "кВА";
  const pagination = useStandardPagination(rows.length);

  const columns: ColumnsType<PowerQualityRow> = [
    {
      title: "Время",
      dataIndex: "timestamp",
      width: 200,
      render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "Показания A",
      dataIndex: "active_register",
      width: 160,
      render: (value?: number) => formatNumber(value, 6),
    },
    {
      title: `Энергия A, ${unit}`,
      dataIndex: "active_energy",
      width: 160,
      render: (value?: number) => formatNumber(value, 4),
    },
    {
      title: "Показания R",
      dataIndex: "reactive_register",
      width: 160,
      render: (value?: number) => formatNumber(value, 6),
    },
    {
      title: `Энергия R, ${reactiveUnit}`,
      dataIndex: "reactive_energy",
      width: 180,
      render: (value?: number) => formatNumber(value, 4),
    },
    {
      title: "cos φ",
      dataIndex: "cos_phi",
      width: 120,
      render: (value?: number) => formatNumber(value, 3),
    },
    {
      title: "tg φ",
      dataIndex: "tg_phi",
      width: 120,
      render: (value?: number) => formatNumber(value, 3),
    },
    {
      title: `Полная мощность, ${fullPowerUnit}`,
      dataIndex: "full_power",
      width: 180,
      render: (value?: number) => formatNumber(value, 3),
    },
    {
      title: "События",
      dataIndex: "events",
      width: 120,
      render: (value?: number) => value ?? 0,
    },
  ];

  const summaryColumns: ColumnsType<PowerQualitySummary> = [
    { title: "Период", dataIndex: "label", width: 160 },
    {
      title: `Активная, ${unit}`,
      dataIndex: "active_energy",
      width: 160,
      render: (value: number) => formatNumber(value, 4),
    },
    {
      title: `Реактивная, ${reactiveUnit}`,
      dataIndex: "reactive_energy",
      width: 160,
      render: (value: number) => formatNumber(value, 4),
    },
    {
      title: `Средняя активная, ${unit}`,
      dataIndex: "active_average",
      width: 180,
      render: (value: number) => formatNumber(value, 4),
    },
    {
      title: `Средняя реактивная, ${reactiveUnit}`,
      dataIndex: "reactive_average",
      width: 180,
      render: (value: number) => formatNumber(value, 4),
    },
    {
      title: `Полная мощность, ${fullPowerUnit}`,
      dataIndex: "full_power",
      width: 200,
      render: (value: number) => formatNumber(value, 3),
    },
  ];

  const ready =
    regionID !== undefined &&
    objectID !== undefined &&
    selectedChannel !== undefined &&
    dateISO !== undefined;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/power-quality", {
        region: regionID,
        object: objectID,
        channel: selectedChannel,
        date: dateISO,
        measurement,
        step,
        use_registers: useRegisters || undefined,
        use_mwt: useMWT || undefined,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const channelLabel =
    channelOptions.find((option) => option.value === selectedChannel)?.label ??
    (selectedChannel ? `Счётчик №${selectedChannel}` : "—");
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;
  const stepLabel = stepOptions.find((option) => option.value === step)?.label ?? `${step} мин`;
  const unitLabel = unitOptions.find((option) => option.value === unitMode)?.label ?? unitMode;

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    const dateLabel = period.from ? period.from.format("DD.MM.YYYY") : "—";
    openComposer({
      subject: `Cos φ и мощность — ${objectLabel}`,
      body: `Отчёт: Показания, cos φ, tg φ\nОбъект: ${objectLabel}\nСчётчик: ${channelLabel}\nДата: ${dateLabel}\nПоказатель: ${measurementLabel}\nШаг: ${stepLabel}\nЕдиницы: ${unitLabel}\nРасчёт по показаниям: ${useRegisters ? "Да" : "Нет"}\n\nВо вложении прикреплён XLSX.`,
      filename: `power-quality_${selectedChannel ?? "channel"}_${period.from?.format("YYYYMMDD") ?? "date"}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    objectID,
    channelLabel,
    measurementLabel,
    stepLabel,
    unitLabel,
    openComposer,
    selectedChannel,
    period.from,
    useRegisters,
  ]);

  return (
    <PageLayoutWithHelp
      title="Показания, cos φ, tg φ и полная мощность"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <ChannelSelector
              options={channelOptions}
              value={selectedChannel}
              loading={channelsQuery.isLoading}
              onChange={(value) => setSelectedChannel(value)}
            />
            <Select
              style={{ width: 200 }}
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
            />
            <Select
              style={{ width: 160 }}
              options={stepOptions}
              value={step}
              onChange={(value) => setStep(value)}
            />
          </FilterRow>
          <FilterRow>
            <Radio.Group
              options={unitOptions}
              optionType="button"
              buttonStyle="solid"
              value={unitMode}
              onChange={(e) => setUnitMode(e.target.value)}
            />
            <Checkbox checked={useRegisters} onChange={(e) => setUseRegisters(e.target.checked)}>
              Рассчитать энергию по показаниям
            </Checkbox>
          </FilterRow>
          <div style={{ width: "100%" }}>
            <PeriodFilter value={period} onChange={setPeriod} showMonthControls={false} />
          </div>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Выберите объект, счётчик и дату, чтобы построить отчёт."
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && (
          <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            <EnterpriseTable<PowerQualityRow>
              rowKey={(row) => row.timestamp}
              columns={columns}
              dataSource={rows}
              loading={powerQualityQuery.isLoading}
              pagination={pagination}
              scroll={{ x: 1200 }}
              size="small"
              toolbar={{ reload: true, onReload: () => powerQualityQuery.refetch() }}
            />
            {summary.length > 0 && (
              <EnterpriseTable<PowerQualitySummary>
                size="small"
                rowKey={(row) => row.label}
                columns={summaryColumns}
                dataSource={summary}
                pagination={false}
                scroll={{ x: 800 }}
                toolbar={{ reload: false }}
              />
            )}
          </Space>
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsPowerQualityPage;
